import { Box, Divider, Flex, Stack } from "@chakra-ui/react";
import { useNavigate } from "react-router";

import { dayjs } from "@equidefi/i18n";
import { InquiryStatusBadge } from "@equidefi/portals/components/InquiryStatusBadge";
import { blankIfNA } from "@equidefi/shared/helpers/string";
import type { DTO } from "@equidefi/shared/types";
import { Heading, Text } from "@equidefi/ui";

import { Row, SectionTitle, VaultContainer, VerificationResult } from "../../../components";
import { SectionButton } from "../../components";

type Props = { inquiry: DTO.FullInquiry };

const InquiryKYBDetails = ({ inquiry }: Props) => {
  const navigate = useNavigate();

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <SectionButton onClick={() => navigate(-1)} iconDirection="left">
        Back To Identity Check Details
      </SectionButton>

      <Box bg="white" borderRadius="md" boxShadow="md" p="4">
        <Flex align="start" justify="space-between" gap="3" mb="3">
          <Box minW={0}>
            <Heading fontWeight="bold" as="h1" mb="1">
              {inquiry.display_name ?? "N/A"}
            </Heading>
            <Text textStyle="body2" color="equidefi.blue" mb="0">
              Last activity: {inquiry.modify_date ? dayjs(inquiry.modify_date).format("MMM Do, YYYY") : "N/A"}
            </Text>
          </Box>
          <InquiryStatusBadge status={inquiry.status} />
        </Flex>

        <Divider my="4" />

        <Stack spacing="2" mb="4">
          <SectionTitle>Entity Information</SectionTitle>
          <Row label="Entity Name" value={inquiry.entity_info?.name} />
        </Stack>

        <Divider my="4" />
        <Stack spacing="2" mb="4">
          <SectionTitle>Entity Details</SectionTitle>
          <Row label="EIN" value={inquiry.entity_details?.ein} />
        </Stack>

        <Divider my="4" />
        <Stack spacing="1" mb="4">
          <SectionTitle>Entity Address</SectionTitle>
          <Row
            label="Address"
            value={
              [
                inquiry.entity_address?.street_1,
                inquiry.entity_address?.street_2,
                inquiry.entity_address?.city,
                inquiry.entity_address?.state,
                blankIfNA(inquiry.entity_address?.postal_code),
                inquiry.entity_address?.country,
              ]
                .filter(Boolean)
                .join(", ") || "N/A"
            }
          />
        </Stack>

        {inquiry.verification && (
          <>
            <Divider my="4" />
            <Stack spacing="2" mb="4">
              <SectionTitle>Verification Results</SectionTitle>
              <VerificationResult passed={!!inquiry.verification.passed} status={inquiry.status} />
            </Stack>
          </>
        )}

        {inquiry.decline_reason && (
          <>
            <Divider my="4" />
            <Stack spacing="2">
              <SectionTitle>Decline Reason</SectionTitle>
              <Box bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200" p="3">
                <Text fontSize="sm" mb="0">
                  {inquiry.decline_reason}
                </Text>
              </Box>
            </Stack>
          </>
        )}
      </Box>
    </VaultContainer>
  );
};

export default InquiryKYBDetails;
